/**
 * lib/clockingPdf.ts
 * ------------------------------------------------------------------
 * HR DOMAIN — the Clocking sheet on A4 landscape (Nov 2026): the school's
 * letterhead and footer (lib/pdfChrome), names down the left, Monday to Saturday
 * across. Each day is drawn by hand because its first line is bold and the rest
 * are not, which a table cell's own text cannot do. What a day says is decided
 * in lib/clockingExport.
 *
 * The signature line prints once, on the last page, where the checker signs for
 * the whole document. Room for it is kept on every page, so it always fits.
 * ------------------------------------------------------------------ */
import { jsPDF } from 'jspdf';
import autoTable, { type CellDef, type RowInput } from 'jspdf-autotable';
import { CONTENT_TOP, PDF, drawLetterhead, pageGeom, stampFooters, type RGB } from '@/lib/pdfChrome';
import { fmtMinutes, weekday } from '@/lib/clockingRules';
import type { CellKind } from '@/lib/clockingRegister';
import {
  exportDayLabel, monthParts, printedDay, type ClockingExportRow, type DayRoom, type MonthPdfLayout,
} from '@/lib/clockingExport';

const FILL: Partial<Record<CellKind, RGB>> = {
  late15: [253, 230, 138],
  late45: [253, 186, 116],
  lateMore: [249, 115, 22],
  off: [241, 243, 246],
  leave: [219, 234, 254],
};

/** The table stops here; below it are the key and, on the last page, the signature. */
const TABLE_BOTTOM_MARGIN = 26;

const SIZES: Record<DayRoom, { name: number; job: number; time: number; word: number; rowH: number; lineH: number; head: number }> = {
  wide: { name: 8.5, job: 7, time: 9, word: 8, rowH: 13, lineH: 3.5, head: 8.5 },
  mid: { name: 8.5, job: 7, time: 8, word: 7, rowH: 13, lineH: 3.3, head: 8 },
  tiny: { name: 7, job: 5.5, time: 5.5, word: 5.5, rowH: 10, lineH: 2.4, head: 6.5 },
};

interface PartSpec {
  days: string[];
  withTotals: boolean;
  title: string;
  room: DayRoom;
  nameW: number;
  totalW: [number, number, number, number];
}

function drawPart(
  d: jsPDF, part: PartSpec, rows: ClockingExportRow[], allDays: string[], meta: string,
  isHoliday: (date: string) => boolean,
): void {
  const geom = pageGeom('l');
  const { room, days, withTotals, nameW, totalW } = part;
  const s = SIZES[room];
  const totalsWidth = withTotals ? totalW.reduce((a, b) => a + b, 0) : 0;
  const dayW = (geom.right - geom.MX - nameW - totalsWidth) / days.length;
  const letterhead = () => drawLetterhead(d, { title: 'STAFF CLOCKING', metaLine: `${part.title} · ${meta}`, geom });
  const dayAt = (date: string) => allDays.indexOf(date);

  const head: CellDef[] = [
    { content: 'Staff', styles: { halign: 'left' } },
    ...days.map((date) => ({
      content: room === 'wide' ? exportDayLabel(date).replace(' ', '\n') : `${['S', 'M', 'T', 'W', 'T', 'F', 'S'][weekday(date)]}\n${Number(date.slice(8))}`,
    })),
    ...(withTotals ? ['Days\nlate', 'Time\nlate', 'Missed\npunch', 'No\npunch'].map((content) => ({ content, styles: { fontSize: s.head - 1 } })) : []),
  ];
  const body: RowInput[] = rows.map((r) => [
    '',
    ...days.map(() => ''),
    ...(withTotals ? [
      r.totals.lateDays || '-',
      r.totals.lateMinutes ? fmtMinutes(r.totals.lateMinutes) : '-',
      r.totals.missingPunches || '-',
      r.noPunchDays || '-',
    ] : []),
  ]);

  autoTable(d, {
    head: [head],
    body,
    startY: CONTENT_TOP,
    margin: { left: geom.MX, right: geom.MX, top: CONTENT_TOP, bottom: TABLE_BOTTOM_MARGIN },
    theme: 'grid',
    rowPageBreak: 'avoid',
    styles: { font: 'helvetica', fontSize: s.word, cellPadding: 0.4, lineColor: PDF.RULE, lineWidth: 0.1, textColor: PDF.INK, halign: 'center', valign: 'middle', minCellHeight: s.rowH },
    headStyles: { fillColor: PDF.NAVY, textColor: PDF.WHITE, fontStyle: 'bold', fontSize: s.head, minCellHeight: 8 },
    columnStyles: {
      0: { cellWidth: nameW },
      ...Object.fromEntries(days.map((_, i) => [i + 1, { cellWidth: dayW }])),
      ...(withTotals ? Object.fromEntries(totalW.map((w, i) => [days.length + 1 + i, { cellWidth: w }])) : {}),
    },
    didParseCell: (h) => {
      if (h.section !== 'body') return;
      const dayIdx = h.column.index - 1;
      if (dayIdx >= 0 && dayIdx < days.length) {
        const row = rows[h.row.index];
        const kind = row.cells[dayAt(days[dayIdx])].kind;
        const fill = FILL[kind];
        if (fill) h.cell.styles.fillColor = fill;
      }
    },
    didDrawCell: (h) => {
      if (h.section !== 'body') return;
      const row = rows[h.row.index];
      const { x, y, width, height } = h.cell;
      if (h.column.index === 0) {
        d.setTextColor(...PDF.INK);
        d.setFont('helvetica', 'bold'); d.setFontSize(s.name);
        // A whole name, on two lines if it needs them: this page gets signed.
        const nameLines = d.splitTextToSize(row.name, nameW - 2) as string[];
        const top = y + height / 2 - (nameLines.length === 1 ? 0.4 : s.lineH / 2 + 0.2);
        d.text(nameLines, x + 1, top);
        d.setTextColor(...PDF.MUTED);
        d.setFont('helvetica', 'normal'); d.setFontSize(s.job);
        if (nameLines.length < 3) d.text(d.splitTextToSize(row.jobTitle || '', nameW - 2)[0] as string, x + 1, top + (nameLines.length * s.lineH) - (nameLines.length === 1 ? 0.3 : 0.2));
        return;
      }
      const dayIdx = h.column.index - 1;
      if (dayIdx >= days.length) return;
      const date = days[dayIdx];
      const printed = printedDay(row.cells[dayAt(date)], room, isHoliday(date));
      const cy = y + height / 2 - ((printed.lines.length - 1) * s.lineH) / 2 + 1;
      printed.lines.forEach((l, i) => {
        d.setFont('helvetica', l.bold ? 'bold' : 'normal');
        d.setFontSize(i < 2 && printed.lines.length > 1 ? s.time : s.word);
        d.setTextColor(...PDF.INK);
        d.text(l.text, x + width / 2, cy + i * s.lineH, { align: 'center' });
      });
      if (printed.missing) {
        d.setDrawColor(...PDF.INK);
        d.setLineWidth(0.3);
        d.setLineDashPattern([0.8, 0.6], 0);
        d.rect(x + 0.5, y + 0.5, width - 1, height - 1);
        d.setLineDashPattern([], 0);
      }
    },
    didDrawPage: () => {
      letterhead();
      drawKey(d);
    },
  });
}

/** The colour key, above the footer on every page. */
function drawKey(d: jsPDF): void {
  const geom = pageGeom('l');
  const y = geom.PH - TABLE_BOTTOM_MARGIN + 3.5;
  const items: [RGB | null, string][] = [
    [null, 'Each day: time in, time out, how late'],
    [FILL.late15!, 'Up to 15 min late'],
    [FILL.late45!, '16-45 min'],
    [FILL.lateMore!, 'Over 45 min'],
    [FILL.off!, 'Off (PH = public holiday)'],
    [FILL.leave!, 'Leave'],
  ];
  let x = geom.MX;
  d.setFont('helvetica', 'normal'); d.setFontSize(7); d.setTextColor(...PDF.MUTED);
  for (const [fill, label] of items) {
    if (fill) {
      d.setFillColor(...fill); d.setDrawColor(...PDF.RULE); d.setLineWidth(0.1);
      d.rect(x, y - 2.3, 2.8, 2.8, 'FD');
      x += 3.6;
    }
    d.text(label, x, y);
    x += d.getTextWidth(label) + 5;
  }
  d.setDrawColor(...PDF.INK); d.setLineWidth(0.3); d.setLineDashPattern([0.8, 0.6], 0);
  d.rect(x, y - 2.3, 2.8, 2.8);
  d.setLineDashPattern([], 0);
  d.text('Missing a punch', x + 3.6, y);
}

function drawSignature(d: jsPDF): void {
  const geom = pageGeom('l');
  const y = geom.PH - 11.5;
  const field = (label: string, x: number, width: number) => {
    d.setFont('helvetica', 'normal'); d.setFontSize(8.5); d.setTextColor(...PDF.INK);
    d.text(label, x, y);
    const from = x + d.getTextWidth(label) + 2;
    d.setDrawColor(...PDF.INK); d.setLineWidth(0.2);
    d.line(from, y + 0.4, x + width, y + 0.4);
  };
  d.setPage(d.getNumberOfPages());
  field('Checked by', geom.MX, 95);
  field('Signature', geom.MX + 103, 85);
  field('Date', geom.MX + 196, geom.right - geom.MX - 196);
}

export interface ClockingPdfInput {
  view: 'week' | 'month';
  /** "26 - 31 Oct 2026" or "October 2026". */
  period: string;
  /** Every Monday-to-Saturday date shown; each row's cells are in this order. */
  days: string[];
  rows: ClockingExportRow[];
  /** Staff type and sort, so a printout says what it covers. */
  scope: string;
  uploadedTo: string;
  monthLayout: MonthPdfLayout;
  isHoliday: (date: string) => boolean;
}

export function buildClockingPdf(x: ClockingPdfInput): jsPDF {
  const d = new jsPDF({ orientation: 'l', unit: 'mm', format: 'a4' });
  const geom = pageGeom('l');
  const meta = `${x.scope} · Uploaded to ${x.uploadedTo}`;
  const parts: PartSpec[] = x.view === 'week'
    ? [{ days: x.days, withTotals: true, title: `Week of ${x.period}`, room: 'wide', nameW: 42, totalW: [10, 21, 13, 11] }]
    : monthParts(x.days, x.monthLayout).map((p) => ({
      ...p,
      title: p.label ? `${x.period}, ${p.label}` : x.period,
      room: x.monthLayout === 'one' ? 'tiny' as const : 'mid' as const,
      nameW: x.monthLayout === 'one' ? 33 : p.withTotals ? 38 : 42,
      totalW: x.monthLayout === 'one' ? [6, 13, 8, 8] : [9, 16, 11, 9],
    }));
  parts.forEach((part, i) => {
    if (i > 0) d.addPage('a4', 'l');
    drawPart(d, part, x.rows, x.days, meta, x.isHoliday);
  });
  drawSignature(d);
  stampFooters(d, { geom, generatedBy: 'Confidential' });
  return d;
}
